/**
 * WeaponSystem: inventory + selection, hitscan firing (raycast against city,
 * vehicles, peds), melee, ammo/reload, pickups and explosion damage.
 * Reads input every frame; runs AFTER player/peds/vehicles in the main loop.
 */
import * as THREE from 'three';
import { G } from './ctx';
import type { DamageSource, IPed, IVehicle, IWeaponSystem, WeaponDef, WeaponId } from './types';
import type { ParticleSystem } from './particles';
import { makeGlow, makeIcon, pickSpots, type PickupKind } from './weapons_pickups';

export const WEAPONS: Record<WeaponId, WeaponDef> = {
  fist: { id: 'fist', name: 'Fists', damage: 10, rate: 2, range: 1.6, spread: 0, pellets: 1, auto: false, clip: 0, held: 'none' },
  bat: { id: 'bat', name: 'Bat', damage: 30, rate: 1.5, range: 2, spread: 0, pellets: 1, auto: false, clip: 0, held: 'bat' },
  pistol: { id: 'pistol', name: 'Pistol', damage: 25, rate: 4, range: 60, spread: 0.02, pellets: 1, auto: false, clip: 12, held: 'pistol' },
  smg: { id: 'smg', name: 'SMG', damage: 14, rate: 11, range: 50, spread: 0.05, pellets: 1, auto: true, clip: 30, held: 'rifle' },
  shotgun: { id: 'shotgun', name: 'Shotgun', damage: 12, rate: 1.1, range: 25, spread: 0.12, pellets: 8, auto: false, clip: 6, held: 'rifle' },
  rifle: { id: 'rifle', name: 'Rifle', damage: 32, rate: 7, range: 90, spread: 0.025, pellets: 1, auto: true, clip: 30, held: 'rifle' },
};

/** slot order for keys 1..6 and next/prev cycling */
export const WEAPON_ORDER: WeaponId[] = ['fist', 'bat', 'pistol', 'smg', 'shotgun', 'rifle'];

const RELOAD_SECONDS = 1.4;
const CRIME_THROTTLE = 1.5;
const WITNESS_RANGE = 45;
const PICKUP_RADIUS = 1.3;
const PICKUP_RESPAWN = 120;
const PICKUP_COUNT = 30;
const PICKUP_MIN_DIST = 40;

const RECOIL: Record<WeaponId, number> = { fist: 0, bat: 0, pistol: 0.04, smg: 0.03, shotgun: 0.08, rifle: 0.05 };
const MUZZLE_INTENSITY: Record<WeaponId, number> = { fist: 0, bat: 0, pistol: 28, smg: 22, shotgun: 45, rifle: 32 };
const PICKUP_AMMO: Record<WeaponId, number> = { fist: 0, bat: 0, pistol: 24, smg: 60, shotgun: 12, rifle: 60 };

export interface Pickup {
  x: number;
  z: number;
  kind: PickupKind;
  active: boolean;
  /** weapon id for kind 'weapon' */
  weapon?: WeaponId;
  /** cash amount for kind 'cash' */
  amount: number;
  respawnAt: number;
  group: THREE.Group;
  icon: THREE.Mesh;
  phase: number;
}

type HitKind = 'none' | 'static' | 'vehicle' | 'ped';

interface BulletHit {
  kind: HitKind;
  dist: number;
  point: THREE.Vector3;
  normal: THREE.Vector3;
  ped: IPed | null;
  vehicle: IVehicle | null;
}

/** state check that survives calls which may mutate the ped (defeats TS narrowing) */
const isDead = (q: IPed): boolean => q.state === 'dead';

// module-level temporaries (never allocated in hot paths)
const _o = new THREE.Vector3();
const _d = new THREE.Vector3();
const _r = new THREE.Vector3();
const _u = new THREE.Vector3();
const _m = new THREE.Vector3();
const _sd = new THREE.Vector3();
const _n = new THREE.Vector3();
const _end = new THREE.Vector3();
const _p = new THREE.Vector3();

export class WeaponSystem implements IWeaponSystem {
  current: WeaponId = 'fist';
  ammo: Record<WeaponId, number> = { fist: Infinity, bat: Infinity, pistol: 0, smg: 0, shotgun: 0, rifle: 0 };
  owned: Record<WeaponId, boolean> = { fist: true, bat: false, pistol: false, smg: false, shotgun: false, rifle: false };
  defs = WEAPONS;

  /** rounds currently loaded, per weapon (melee = 0) */
  clips: Record<WeaponId, number> = { fist: 0, bat: 0, pistol: 0, smg: 0, shotgun: 0, rifle: 0 };
  reloading = false;
  /** 0..1 while reloading */
  reloadProgress = 0;
  /** world pickups (HUD blips read x/z/kind/active) */
  pickups: Pickup[] = [];
  /** last hit info of the most recent bullet (debug / tests) */
  lastHit: { kind: HitKind; x: number; y: number; z: number; dist: number } = { kind: 'none', x: 0, y: 0, z: 0, dist: 0 };
  /** stats */
  shotsFired = 0;

  private reloadT = 0;
  private cooldown = 0;
  private prevFire = false;
  private queuedUntil = -1;
  private lastCrimeAt = -10;
  private dryClickAt = -10;
  private readonly muzzleLight: THREE.PointLight;
  private muzzleT = 0;
  private muzzlePeak = 0;
  private meleeHitAt = -1;
  private comboPed: IPed | null = null;
  private comboN = 0;
  private comboT = -10;
  private readonly ray = new THREE.Raycaster();
  private readonly targets: THREE.Object3D[] = [];
  private readonly hit: BulletHit = { kind: 'none', dist: 0, point: new THREE.Vector3(), normal: new THREE.Vector3(), ped: null, vehicle: null };
  private readonly pickupRoot = new THREE.Group();

  constructor() {
    // clip of the starting weapon
    this.muzzleLight = new THREE.PointLight(0xffb964, 0, 14, 2);
    this.muzzleLight.castShadow = false;
    this.muzzleLight.name = 'muzzle-light';
    G.scene.add(this.muzzleLight);
    this.pickupRoot.name = 'pickups';
    G.scene.add(this.pickupRoot);
    this.spawnPickups();
    this.applyHeld();
  }

  // ------------------------------------------------------------- getters ----

  /** rounds in the current clip */
  get clip(): number {
    return this.clips[this.current];
  }
  /** rounds outside the clip for the current weapon (Infinity for melee) */
  get reserve(): number {
    const a = this.ammo[this.current];
    return a === Infinity ? Infinity : Math.max(0, a - this.clips[this.current]);
  }
  get currentDef(): WeaponDef {
    return this.defs[this.current];
  }

  canFire(): boolean {
    const p = G.player;
    if (!p || G.controlsLocked || p.dead || p.transitioning || p.vehicle) return false;
    if (this.reloading || this.cooldown > 0) return false;
    const def = this.defs[this.current];
    if (def.clip === 0) return true;
    return this.clips[this.current] > 0;
  }

  // ----------------------------------------------------------- inventory ----

  give(id: WeaponId, ammo: number): void {
    const def = this.defs[id];
    const wasOwned = this.owned[id];
    this.owned[id] = true;
    if (def.clip > 0) {
      this.ammo[id] = Math.min(999, (this.ammo[id] === Infinity ? 0 : this.ammo[id]) + Math.max(0, ammo));
      if (this.clips[id] <= 0) this.clips[id] = Math.min(def.clip, this.ammo[id]);
    } else this.ammo[id] = Infinity;
    const curMelee = this.defs[this.current].clip === 0;
    if (curMelee && id !== this.current && (def.clip > 0 || this.current === 'fist')) this.select(id, !wasOwned);
  }

  select(id: WeaponId, silent = false): void {
    if (!this.owned[id]) return;
    if (id === this.current) return;
    this.current = id;
    this.reloading = false;
    this.reloadProgress = 0;
    this.reloadT = 0;
    this.queuedUntil = -1;
    this.cooldown = Math.max(this.cooldown, 0.15);
    this.applyHeld();
    if (!silent) G.audio.play('ui_click');
  }

  private cycle(dir: 1 | -1): void {
    const i = WEAPON_ORDER.indexOf(this.current);
    for (let k = 1; k <= WEAPON_ORDER.length; k++) {
      const id = WEAPON_ORDER[(i + dir * k + WEAPON_ORDER.length * 2) % WEAPON_ORDER.length];
      if (this.owned[id]) {
        if (id !== this.current) this.select(id);
        return;
      }
    }
  }

  strip(): void {
    for (const id of WEAPON_ORDER) {
      const def = this.defs[id];
      this.owned[id] = id === 'fist';
      this.ammo[id] = def.clip === 0 ? Infinity : 0;
      this.clips[id] = 0;
    }
    this.current = 'fist';
    this.reloading = false;
    this.reloadProgress = 0;
    this.applyHeld();
  }

  private applyHeld(): void {
    const p = G.player;
    if (p && p.humanoid) p.humanoid.setHeldWeapon(this.defs[this.current].held);
  }

  serialize(): { current: WeaponId; ammo: Record<WeaponId, number>; owned: Record<WeaponId, boolean> } {
    return { current: this.current, ammo: { ...this.ammo }, owned: { ...this.owned } };
  }

  restore(s: ReturnType<IWeaponSystem['serialize']>): void {
    if (!s) return;
    for (const id of WEAPON_ORDER) {
      const def = this.defs[id];
      const own = s.owned && typeof s.owned[id] === 'boolean' ? s.owned[id] : id === 'fist';
      this.owned[id] = own || id === 'fist';
      if (def.clip === 0) this.ammo[id] = Infinity;
      else {
        const a = s.ammo ? Number(s.ammo[id]) : 0;
        this.ammo[id] = Number.isFinite(a) && a > 0 ? Math.floor(a) : 0;
      }
      this.clips[id] = def.clip === 0 ? 0 : Math.min(def.clip, this.ammo[id]);
    }
    const cur = s.current && this.owned[s.current] ? s.current : 'fist';
    this.current = cur;
    this.reloading = false;
    this.reloadProgress = 0;
    this.applyHeld();
  }

  // ---------------------------------------------------------------- loop ----

  update(dt: number): void {
    const p = G.player;
    const inp = G.input;
    const t = G.time.t;

    // muzzle flash decay
    if (this.muzzleT > 0) {
      this.muzzleT -= dt;
      this.muzzleLight.intensity = this.muzzleT > 0 ? (this.muzzleT / 0.07) * this.muzzlePeak : 0;
    }

    // selection (allowed even when firing is blocked, except when controls are locked / dead)
    if (!G.controlsLocked && !p.dead) {
      const slot = inp.weaponSlot;
      if (slot >= 1 && slot <= WEAPON_ORDER.length) {
        const id = WEAPON_ORDER[slot - 1];
        if (this.owned[id] && id !== this.current) this.select(id);
      }
      if (inp.nextWeapon) this.cycle(1);
      if (inp.prevWeapon) this.cycle(-1);
    }

    // reload progress
    if (this.reloading) {
      this.reloadT += dt;
      this.reloadProgress = Math.min(1, this.reloadT / RELOAD_SECONDS);
      if (this.reloadT >= RELOAD_SECONDS) this.finishReload();
    }

    this.updatePickups(t);

    // melee follow-through
    if (this.meleeHitAt >= 0 && t >= this.meleeHitAt) {
      this.meleeHitAt = -1;
      this.meleeHit();
    }
    if (this.cooldown > 0) this.cooldown -= dt;

    const blocked = G.controlsLocked || p.dead || p.transitioning || !!p.vehicle;
    const fireRaw = inp.fire;
    const pressed = fireRaw && !this.prevFire;
    this.prevFire = fireRaw;
    if (blocked) {
      this.queuedUntil = -1;
      return;
    }

    const def = this.defs[this.current];
    if (pressed) this.queuedUntil = t + 0.25;
    const wants = def.auto ? fireRaw : pressed || this.queuedUntil > t;
    if (!wants || this.cooldown > 0) return;

    if (def.clip === 0) {
      this.queuedUntil = -1;
      this.swing(def);
      return;
    }
    if (this.reloading) return;
    if (this.clips[this.current] > 0) {
      this.queuedUntil = -1;
      this.shoot(def);
    } else if (this.reserve > 0) {
      this.queuedUntil = -1;
      this.startReload();
    } else if (pressed && t - this.dryClickAt > 0.25) {
      this.queuedUntil = -1;
      this.dryClickAt = t;
      G.audio.play('ui_click');
    }
  }

  // -------------------------------------------------------------- reload ----

  private startReload(): void {
    if (this.reloading) return;
    this.reloading = true;
    this.reloadT = 0;
    this.reloadProgress = 0;
    G.audio.play('ui_click');
  }

  private finishReload(): void {
    const id = this.current;
    const def = this.defs[id];
    this.reloading = false;
    this.reloadProgress = 0;
    this.reloadT = 0;
    if (def.clip === 0) return;
    const reserve = Math.max(0, this.ammo[id] - this.clips[id]);
    this.clips[id] += Math.min(def.clip - this.clips[id], reserve);
    G.audio.play('ui_click');
  }

  // --------------------------------------------------------------- shoot ----

  private shoot(def: WeaponDef): void {
    const p = G.player;
    const id = def.id;
    this.clips[id]--;
    this.ammo[id]--;
    this.cooldown = 1 / def.rate;
    this.shotsFired++;

    const origin = p.eyePosition(_o);
    const dir = p.aimDirection(_d);
    if (dir.lengthSq() < 1e-6) dir.set(Math.sin(p.heading), 0, Math.cos(p.heading));
    dir.normalize();
    // right-hand side of the shooter (matches CameraController: right = (-cos yaw, 0, sin yaw))
    const right = _r.set(-dir.z, 0, dir.x);
    if (right.lengthSq() < 1e-6) right.set(-1, 0, 0);
    right.normalize();
    const up = _u.crossVectors(right, dir).normalize();
    const muzzle = _m.copy(origin).addScaledVector(dir, 0.6).addScaledVector(right, 0.25);
    muzzle.y = p.pos.y + 1.35;

    const rng = G.rng;
    let anyHit = false;
    for (let k = 0; k < def.pellets; k++) {
      const sx = (rng() - 0.5) * 2 * def.spread;
      const sy = (rng() - 0.5) * 2 * def.spread;
      _sd.copy(dir).addScaledVector(right, sx).addScaledVector(up, sy).normalize();
      const h = this.castBullet(origin, _sd, def.range);
      if (h.kind === 'none') {
        _end.copy(origin).addScaledVector(_sd, def.range);
      } else {
        _end.copy(h.point);
        anyHit = true;
        this.applyHit(def, h, _sd);
      }
      G.particles.tracer(muzzle.x, muzzle.y, muzzle.z, _end.x, _end.y, _end.z);
      if (k === 0) {
        this.lastHit.kind = h.kind;
        this.lastHit.x = _end.x;
        this.lastHit.y = _end.y;
        this.lastHit.z = _end.z;
        this.lastHit.dist = h.kind === 'none' ? def.range : h.dist;
      }
    }
    void anyHit;

    // muzzle flash + light + recoil + sfx
    G.particles.emit('muzzle', muzzle.x, muzzle.y, muzzle.z, def.id === 'shotgun' ? 5 : 3, dir.x, dir.y, dir.z);
    this.muzzleLight.position.copy(muzzle);
    this.muzzlePeak = MUZZLE_INTENSITY[id];
    this.muzzleLight.intensity = this.muzzlePeak;
    this.muzzleT = 0.07;
    G.cam.shake(RECOIL[id], 0.1);
    G.audio.play(('shot_' + id) as 'shot_pistol', p.pos.x, p.pos.z);
    G.events.emit('shot', { weapon: id, x: p.pos.x, z: p.pos.z, by: 'player' });
    this.reportShotCrime(p.pos.x, p.pos.z);

    if (this.clips[id] <= 0 && this.reserve > 0) this.startReload();
  }

  private reportShotCrime(x: number, z: number): void {
    const t = G.time.t;
    if (t - this.lastCrimeAt < CRIME_THROTTLE) return;
    const peds = G.entities.peds;
    const r2 = WITNESS_RANGE * WITNESS_RANGE;
    for (let i = 0; i < peds.length; i++) {
      const q = peds[i];
      if (q.state === 'dead') continue;
      const dx = q.pos.x - x, dz = q.pos.z - z;
      if (dx * dx + dz * dz <= r2) {
        this.lastCrimeAt = t;
        G.events.emit('crime', { kind: 'shot_fired', x, z });
        return;
      }
    }
  }

  /** hitscan against city meshes + colliders + ground, vehicles and alive peds. Result object is reused. */
  private castBullet(origin: THREE.Vector3, dir: THREE.Vector3, range: number): BulletHit {
    const h = this.hit;
    h.kind = 'none';
    h.dist = Infinity;
    h.ped = null;
    h.vehicle = null;
    const city = G.city;
    const targets = this.targets;
    targets.length = 0;

    const rt = city.raycastTargets;
    for (let i = 0; i < rt.length; i++) targets.push(rt[i]);

    const vehicles = G.entities.vehicles;
    const r2 = (range + 8) * (range + 8);
    for (let i = 0; i < vehicles.length; i++) {
      const v = vehicles[i];
      const dx = v.pos.x - origin.x, dz = v.pos.z - origin.z;
      if (dx * dx + dz * dz > r2) continue;
      if (dx * dir.x + dz * dir.z < -8) continue; // fully behind
      targets.push(v.group);
    }
    const peds = G.entities.peds;
    for (let i = 0; i < peds.length; i++) {
      const q = peds[i];
      if (q.state === 'dead' || q.vehicle || !q.group.visible) continue;
      const dx = q.pos.x - origin.x, dz = q.pos.z - origin.z;
      if (dx * dx + dz * dz > r2) continue;
      if (dx * dir.x + dz * dir.z < -2) continue;
      targets.push(q.group);
    }

    this.ray.set(origin, dir);
    this.ray.near = 0.05;
    this.ray.far = range;
    const hits = this.ray.intersectObjects(targets, true);
    for (let i = 0; i < hits.length; i++) {
      const it = hits[i];
      const obj = it.object as THREE.Mesh;
      if (!obj.isMesh) continue;
      if (it.distance >= h.dist) break;
      // owner lookup
      let o: THREE.Object3D | null = obj;
      let ent: unknown = null;
      while (o) {
        if (o.userData && o.userData.entity) {
          ent = o.userData.entity;
          break;
        }
        o = o.parent;
      }
      const kind = o && o.userData.kind;
      if (ent && kind === 'ped') {
        const q = ent as IPed;
        if (q.state === 'dead') continue;
        h.kind = 'ped';
        h.ped = q;
      } else if (ent && kind === 'vehicle') {
        h.kind = 'vehicle';
        h.vehicle = ent as IVehicle;
      } else if (ent && (ent as IPed).humanoid) {
        h.kind = 'ped';
        h.ped = ent as IPed;
      } else if (ent && (ent as IVehicle).spec) {
        h.kind = 'vehicle';
        h.vehicle = ent as IVehicle;
      } else h.kind = 'static';
      h.dist = it.distance;
      h.point.copy(it.point);
      if (it.face) h.normal.copy(it.face.normal).transformDirection(obj.matrixWorld);
      else h.normal.copy(dir).negate();
      if (h.normal.dot(dir) > 0) h.normal.negate();
      break;
    }

    // analytic ground plane (y = 0)
    if (dir.y < -1e-4) {
      const tg = -origin.y / dir.y;
      if (tg > 0.05 && tg < range && tg < h.dist) {
        h.kind = 'static';
        h.ped = null;
        h.vehicle = null;
        h.dist = tg;
        h.point.copy(origin).addScaledVector(dir, tg);
        h.normal.set(0, 1, 0);
      }
    }
    // analytic colliders (AABB slabs) as a safety net for geometry not in raycastTargets
    const cols = city.colliders;
    if (cols.length > 0 && cols.length < 6000) {
      const ix = 1 / (dir.x || 1e-9), iy = 1 / (dir.y || 1e-9), iz = 1 / (dir.z || 1e-9);
      let bestT = Math.min(h.dist, range);
      let bestBox = -1;
      for (let i = 0; i < cols.length; i++) {
        const c = cols[i];
        let t1 = (c.minX - origin.x) * ix, t2 = (c.maxX - origin.x) * ix;
        let tmin = Math.min(t1, t2), tmax = Math.max(t1, t2);
        t1 = (0 - origin.y) * iy;
        t2 = (c.height - origin.y) * iy;
        tmin = Math.max(tmin, Math.min(t1, t2));
        tmax = Math.min(tmax, Math.max(t1, t2));
        if (tmax < tmin) continue;
        t1 = (c.minZ - origin.z) * iz;
        t2 = (c.maxZ - origin.z) * iz;
        tmin = Math.max(tmin, Math.min(t1, t2));
        tmax = Math.min(tmax, Math.max(t1, t2));
        if (tmax < tmin || tmax < 0.05) continue;
        const tt = tmin > 0.05 ? tmin : -1;
        if (tt > 0 && tt < bestT) {
          bestT = tt;
          bestBox = i;
        }
      }
      if (bestBox >= 0) {
        const c = cols[bestBox];
        h.kind = 'static';
        h.ped = null;
        h.vehicle = null;
        h.dist = bestT;
        h.point.copy(origin).addScaledVector(dir, bestT);
        // face normal from the dominant penetration axis
        const px = h.point.x, py = h.point.y, pz = h.point.z;
        const e = 0.02;
        if (Math.abs(px - c.minX) < e) h.normal.set(-1, 0, 0);
        else if (Math.abs(px - c.maxX) < e) h.normal.set(1, 0, 0);
        else if (Math.abs(pz - c.minZ) < e) h.normal.set(0, 0, -1);
        else if (Math.abs(pz - c.maxZ) < e) h.normal.set(0, 0, 1);
        else if (Math.abs(py - c.height) < e) h.normal.set(0, 1, 0);
        else h.normal.copy(dir).negate();
      }
    }
    return h;
  }

  private applyHit(def: WeaponDef, h: BulletHit, dir: THREE.Vector3): void {
    const pt = h.point;
    if (h.kind === 'ped' && h.ped) {
      h.ped.applyDamage(def.damage, 'player', _o);
      G.particles.emit('blood', pt.x, pt.y, pt.z, def.id === 'shotgun' ? 4 : 9, dir.x, dir.y, dir.z);
      G.audio.play('ped_hit', pt.x, pt.z, 0.7);
    } else if (h.kind === 'vehicle' && h.vehicle) {
      h.vehicle.applyDamage(def.damage * 0.8, 'player');
      // reflect the bullet direction about the surface normal for the spark spray
      _n.copy(dir).addScaledVector(h.normal, -2 * dir.dot(h.normal));
      G.particles.emit('spark', pt.x, pt.y, pt.z, def.id === 'shotgun' ? 3 : 7, _n.x, _n.y + 0.4, _n.z);
      G.audio.play('hit', pt.x, pt.z, 0.6);
    } else if (h.kind === 'static') {
      _n.copy(dir).addScaledVector(h.normal, -2 * dir.dot(h.normal));
      G.particles.emit('spark', pt.x, pt.y, pt.z, def.id === 'shotgun' ? 2 : 5, _n.x, _n.y + 0.3, _n.z);
      G.particles.emit('dust', pt.x, pt.y, pt.z, def.id === 'shotgun' ? 2 : 4, h.normal.x, h.normal.y, h.normal.z);
      G.audio.play('hit', pt.x, pt.z, 0.5);
    }
  }

  // --------------------------------------------------------------- melee ----

  private swing(def: WeaponDef): void {
    const t = G.time.t;
    this.cooldown = 1 / def.rate;
    this.meleeHitAt = t + 0.12;
    // one-shot: Humanoid restarts the punch on every call, Player leaves it alone while it plays
    G.player.humanoid.setAnim('punch');
  }

  private meleeHit(): void {
    const p = G.player;
    if (p.dead || p.vehicle) return;
    const def = this.defs[this.current];
    if (def.clip !== 0) return;
    const t = G.time.t;
    const dir = p.aimDirection(_d);
    dir.y = 0;
    if (dir.lengthSq() < 1e-6) dir.set(Math.sin(p.heading), 0, Math.cos(p.heading));
    dir.normalize();
    const range = def.range + 0.3;
    const cosHalf = Math.cos((70 * Math.PI) / 360);

    // nearest alive ped inside the cone
    let best: IPed | null = null;
    let bd = range;
    const peds = G.entities.peds;
    for (let i = 0; i < peds.length; i++) {
      const q = peds[i];
      if (q.state === 'dead' || q.vehicle) continue;
      const dx = q.pos.x - p.pos.x, dz = q.pos.z - p.pos.z;
      const d = Math.hypot(dx, dz);
      if (d > bd || d < 1e-4) continue;
      if ((dx * dir.x + dz * dir.z) / d < cosHalf) continue;
      bd = d;
      best = q;
    }
    if (best) {
      const q = best;
      const dx = q.pos.x - p.pos.x, dz = q.pos.z - p.pos.z;
      const d = Math.hypot(dx, dz) || 1;
      const hx = q.pos.x - (dx / d) * 0.2, hz = q.pos.z - (dz / d) * 0.2;
      if (this.current === 'bat') {
        q.applyDamage(def.damage, 'player', p.pos);
        if (!isDead(q)) q.knockDown(dx / d, dz / d, 6, 'player');
        G.particles.emit('blood', hx, 1.25, hz, 6, dx / d, 0.3, dz / d);
        G.audio.play('bat', q.pos.x, q.pos.z);
      } else {
        if (this.comboPed !== q || t - this.comboT > 1.3) this.comboN = 0;
        this.comboPed = q;
        this.comboT = t;
        this.comboN++;
        q.applyDamage(def.damage, 'player', p.pos);
        if (!isDead(q) && this.comboN % 3 === 0) q.knockDown(dx / d, dz / d, 2.5, 'player');
        G.particles.emit('blood', hx, 1.3, hz, 3, dx / d, 0.4, dz / d);
        G.audio.play('punch', q.pos.x, q.pos.z);
      }
      return;
    }

    // bat also dents cars a little
    if (this.current === 'bat') {
      _p.copy(p.pos).addScaledVector(dir, def.range * 0.8);
      const v = G.entities.nearestVehicle(_p.x, _p.z, 4, (vv) => !vv.destroyed && vv.containsPoint(_p.x, _p.z, 0.35));
      if (v) {
        v.applyDamage(6, 'player');
        v.lastPlayerContact = t;
        G.particles.emit('spark', _p.x, 0.8, _p.z, 6, -dir.x, 0.6, -dir.z);
        G.audio.play('bat', _p.x, _p.z);
        return;
      }
    }
    // whiff
    G.audio.play(this.current === 'bat' ? 'bat' : 'punch', p.pos.x, p.pos.z, 0.25);
  }

  // ----------------------------------------------------------- explosion ----

  explosionAt(x: number, y: number, z: number, radius: number, damage: number, source: DamageSource): void {
    const t = G.time.t;
    // visuals unless a composite explosion was just spawned here (Vehicle.explode does that itself)
    const ps = G.particles as ParticleSystem;
    const recent = typeof ps.lastExplosionT === 'number' && t - ps.lastExplosionT < 0.15 && Math.hypot(ps.lastExplosionX - x, ps.lastExplosionZ - z) < 3;
    if (!recent) {
      const size = Math.min(2.5, Math.max(0.8, radius / 4));
      G.particles.explosion(x, y, z, size);
      G.events.emit('explosion', { x, y, z, size });
    }
    const r = Math.max(0.1, radius);

    // peds
    const peds = G.entities.peds;
    for (let i = peds.length - 1; i >= 0; i--) {
      const q = peds[i];
      if (q.state === 'dead' || q.vehicle) continue;
      const dx = q.pos.x - x, dz = q.pos.z - z;
      const d = Math.hypot(dx, dz);
      if (d > r) continue;
      const f = 1 - d / r;
      const nx = d > 1e-4 ? dx / d : 0, nz = d > 1e-4 ? dz / d : 1;
      q.knockDown(nx, nz, 3 + 6 * f, source);
      if (!isDead(q)) q.applyDamage(damage * f, source, _p.set(x, y, z));
    }
    // vehicles
    const vehicles = G.entities.vehicles;
    for (let i = vehicles.length - 1; i >= 0; i--) {
      const v = vehicles[i];
      if (v.destroyed) continue;
      const dx = v.pos.x - x, dz = v.pos.z - z;
      const d = Math.max(0, Math.hypot(dx, dz) - v.spec.length * 0.25);
      if (d > r) continue;
      const f = 1 - d / r;
      v.applyDamage(damage * f, source);
    }
    // player
    const p = G.player;
    if (p && !p.dead) {
      const dx = p.pos.x - x, dz = p.pos.z - z;
      const d = Math.hypot(dx, dz);
      if (d <= r) {
        const f = 1 - d / r;
        p.applyDamage(damage * f * (p.vehicle ? 0.5 : 1), 'explosion');
      }
    }
    if (source === 'player') G.events.emit('crime', { kind: 'explosion', x, z });
  }

  // ------------------------------------------------------------- pickups ----

  private spawnPickups(): void {
    const city = G.city;
    const rng = G.rng;
    const start = city.poi('apartment');
    const first = { x: start.pos.x + Math.sin(start.heading + 0.9) * 12, z: start.pos.z + Math.cos(start.heading + 0.9) * 12 };
    const spots = pickSpots(rng, city, PICKUP_COUNT, PICKUP_MIN_DIST, first);
    const kinds: { kind: PickupKind; weapon?: WeaponId }[] = [];
    const pushN = (n: number, kind: PickupKind, weapon?: WeaponId) => {
      for (let i = 0; i < n; i++) kinds.push({ kind, weapon });
    };
    pushN(5, 'weapon', 'pistol');
    pushN(4, 'weapon', 'smg');
    pushN(3, 'weapon', 'shotgun');
    pushN(2, 'weapon', 'rifle');
    pushN(3, 'weapon', 'bat');
    pushN(5, 'health');
    pushN(4, 'armor');
    pushN(3, 'cash');
    // deterministic shuffle; the first spot (near the apartment) is always a pistol
    for (let i = kinds.length - 1; i > 0; i--) {
      const j = Math.floor(rng() * (i + 1));
      const tmp = kinds[i];
      kinds[i] = kinds[j];
      kinds[j] = tmp;
    }
    kinds.unshift({ kind: 'weapon', weapon: 'pistol' });

    for (let i = 0; i < spots.length && i < kinds.length; i++) {
      const k = kinds[i];
      const s = spots[i];
      const group = new THREE.Group();
      group.position.set(s.x, 0, s.z);
      const icon = makeIcon(k.kind, k.weapon);
      group.add(icon);
      group.add(makeGlow(k.kind));
      this.pickupRoot.add(group);
      this.pickups.push({
        x: s.x,
        z: s.z,
        kind: k.kind,
        active: true,
        weapon: k.weapon,
        amount: k.kind === 'cash' ? 150 + Math.round(rng() * 45) * 10 : 0,
        respawnAt: 0,
        group,
        icon,
        phase: rng() * Math.PI * 2,
      });
    }
  }

  private updatePickups(t: number): void {
    const p = G.player;
    const onFoot = !p.vehicle && !p.dead && !p.transitioning && !G.controlsLocked;
    const px = p.pos.x, pz = p.pos.z;
    const pickups = this.pickups;
    for (let i = 0; i < pickups.length; i++) {
      const pk = pickups[i];
      if (!pk.active) {
        if (t >= pk.respawnAt) {
          pk.active = true;
          pk.group.visible = true;
        }
        continue;
      }
      const dx = pk.x - px, dz = pk.z - pz;
      const d2 = dx * dx + dz * dz;
      if (d2 < 90 * 90) {
        pk.icon.rotation.y = t * 1.4 + pk.phase;
        pk.icon.position.y = 1 + Math.sin(t * 2.2 + pk.phase) * 0.09;
      }
      if (onFoot && d2 <= PICKUP_RADIUS * PICKUP_RADIUS) this.collect(pk, t);
    }
  }

  private collect(pk: Pickup, t: number): void {
    const p = G.player;
    switch (pk.kind) {
      case 'weapon': {
        const id = pk.weapon ?? 'pistol';
        this.give(id, PICKUP_AMMO[id]);
        G.audio.play('weapon_pickup', pk.x, pk.z);
        const def = this.defs[id];
        G.hud.notify(def.clip > 0 ? `${def.name} +${PICKUP_AMMO[id]}` : `${def.name}`);
        break;
      }
      case 'health':
        if (p.health >= 100) return; // leave it for later
        p.heal(50);
        G.audio.play('pickup', pk.x, pk.z);
        break;
      case 'armor':
        if (p.armor >= 100) return;
        p.addArmor(50);
        G.audio.play('pickup', pk.x, pk.z);
        break;
      case 'cash':
        G.addMoney(pk.amount);
        G.audio.play('cash', pk.x, pk.z);
        break;
    }
    G.particles.emit('cash', pk.x, 0.6, pk.z, pk.kind === 'cash' ? 22 : 12);
    G.events.emit('pickup', { kind: pk.kind, x: pk.x, z: pk.z });
    pk.active = false;
    pk.group.visible = false;
    pk.respawnAt = t + PICKUP_RESPAWN;
  }
}
